import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Briefcase, Trophy } from "lucide-react";
import { breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import PageHeader from "@/components/public/PageHeader";
import { Reveal, Spinner } from "@/components/public/Primitives";
import { useLanguage } from "@/context/LanguageContext";
import { getLeadershipRoles } from "@/lib/api";

export default function LeadershipRoles() {
  const { data: roles = [], isLoading } = useQuery({ queryKey: ["leadership-roles"], queryFn: getLeadershipRoles });
  const { tr } = useLanguage();

  return (
    <div data-testid="leadership-roles-page">
      <PageSeo
        slug="leadership-roles"
        title="Leadership Roles | Girish Kumar Naidu"
        description="Leadership roles and positions held by Girish Kumar Naidu across charitable trust, community, and youth organisations."
        keywords="Girish Kumar Naidu leadership roles, positions held, community coordinator"
        schemas={[breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "Leadership Roles" }])]}
      />
      <PageHeader
        overline={tr("page.leadership.overline")}
        title={tr("page.leadership.title")}
        subtitle={tr("page.leadership.subtitle")}
        crumbs={[{ name: "Leadership Roles" }]}
      />

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          {isLoading ? (
            <Spinner />
          ) : (
            <div className="space-y-6">
              {roles.map((r, i) => (
                <Reveal key={r.id} delay={i * 0.06}>
                  <div className="grid md:grid-cols-12 gap-6 bg-paper rounded-3xl p-7 md:p-8 border border-gray-200 card-lift">
                    <div className="md:col-span-3">
                      <div className="rounded-2xl overflow-hidden aspect-video md:aspect-square bg-navy flex items-center justify-center">
                        {r.image ? (
                          <img src={r.image} alt={r.organization} className="h-full w-full object-cover" />
                        ) : (
                          <Briefcase className="h-10 w-10 text-gold" />
                        )}
                      </div>
                    </div>
                    <div className="md:col-span-9">
                      <span className="inline-block px-3 py-1 rounded-full font-mono text-[10px] font-bold uppercase tracking-wider bg-gold/10 text-gold mb-3">{r.duration}</span>
                      <h3 className="font-display text-xl md:text-2xl font-black text-navy tracking-tight">{r.position}</h3>
                      <p className="font-mono text-xs uppercase tracking-wider text-civic mt-1.5">{r.organization}</p>
                      <p className="text-gray-600 mt-4 leading-relaxed">{r.description}</p>
                      {r.achievements && (
                        <div className="flex items-start gap-2 mt-4 text-sm text-gray-700">
                          <Trophy className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                          <p>{r.achievements}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
